import React from 'react';
import { BarChart, Trash2, Layers, Clock } from 'lucide-react';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных комнат
 */
const ComparisonSidebar = ({ 
  savedRooms, 
  deleteSavedRoom, 
  loadSavedRoom 
}) => {
  // Если нет сохраненных комнат
  if (!savedRooms || savedRooms.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-fuchsia-500" />
          Сравнение помещений
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные варианты помещений для сравнения.
          После расчета помещения вы можете сохранить его для последующего сравнения.
        </p>
      </div>
    );
  }

  // Количество дней, прошедших с момента сохранения
  const getDaysAgo = (dateString) => {
    const savedDate = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - savedDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'сегодня';
    if (diffDays === 1) return 'вчера';
    return `${diffDays} ${getDaysPluralForm(diffDays)} назад`;
  };
  
  // Склонение для дней
  const getDaysPluralForm = (days) => {
    if (days >= 11 && days <= 19) return 'дней';
    const lastDigit = days % 10;
    if (lastDigit === 1) return 'день';
    if (lastDigit >= 2 && lastDigit <= 4) return 'дня';
    return 'дней';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-fuchsia-500" />
          Сравнение помещений
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedRooms.length} {savedRooms.length === 1 ? 'помещение' : 
           savedRooms.length < 5 ? 'помещения' : 'помещений'}
        </span>
      </div>

      <div className="space-y-4">
        {savedRooms.map((room) => (
          <div 
            key={room.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {room.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedRoom(room.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedRoom(room.id)}
                  title="Удалить"
                />
              </div>
            </div>
            
            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center mb-2">
              <Clock className="w-3 h-3 mr-1" />
              <span>Сохранено {getDaysAgo(room.date)}</span>
            </div>
            
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Форма:</span>
                <span className="font-medium">
                  {room.roomShape === 'rectangle' ? 'Прямоугольная' : 
                   room.roomShape === 'l-shaped' ? 'Г-образная' : 
                   room.roomShape === 'angled' ? 'Со скошенным углом' :
                   room.roomShape === 'bay-window' ? 'С эркером' :
                   room.roomShape === 'niche' ? 'С нишей' :
                   room.roomShape === 'column' ? 'С колонной' :
                   room.roomShape === 'polygon' ? 'Произвольная форма' : 'Круглая'}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Площадь пола:</span>
                <span className="font-medium">
                  {formatNumber(room.results.floorArea, 2)} м²
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Площадь стен:</span>
                <span>
                  {formatNumber(room.results.wallArea, 2)} м²
                </span>
              </div>
              <div className="flex justify-between">
                <span>Стоимость материалов:</span>
                <span className="font-semibold text-fuchsia-600 dark:text-fuchsia-400">
                  {formatCurrency(room.results.totalMaterialCost, 0)} ₽
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {savedRooms.length >= 2 && (
        <div className="mt-4 p-3 bg-fuchsia-50 dark:bg-fuchsia-900/20 border border-fuchsia-100 dark:border-fuchsia-800 rounded-lg">
          <p className="text-xs text-fuchsia-700 dark:text-fuchsia-300">
            <span className="font-semibold">Совет:</span> Чтобы сравнить помещения по стоимости ремонта, 
            используйте одинаковые типы отделочных материалов для корректного сопоставления.
          </p>
        </div>
      )}
    </div>
  );
};

export default ComparisonSidebar;